import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {WidgetConfiguration} from "./widget-configuration";
import {SnappingOptionsPropertiesConfiguration} from "./snapping-options-properties-configuration";
import {SketchTooltipOptionsPropertiesConfiguration} from "./sketch-tooltip-options-properties-configuration";
import {SketchLabelOptionsPropertiesConfiguration} from "./sketch-label-options-properties-configuration";
import {EditorVisibleElementsConfiguration} from "./editor-visible-elements-configuration";
import {SketchValueOptionsConfiguration} from "./sketch-configuration";
import {EditorLayerInfoConfiguration} from "./editor-layer-info-configuration";


@JsonObject()
export class EditorConfiguration extends WidgetConfiguration {

    /**
     * An array of string values which specifies what end users are allowed to edit.
     *
     * @deprecated since version 4.29. Use {@link module:esri/widgets/Editor#visibleElements Editor.visibleElements} instead.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#allowedWorkflows)
     */
    @JsonProperty()
    declare allowedWorkflows?: "create-features" | "update";
    /**
     * Indicates the heading level to use for title of the widget.
     *
     * @default 4
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#headingLevel)
     */
    @JsonProperty()
    declare headingLevel?: number;
    /**
     * Options to configure the sketch labels shown next to each segment of the geometry being created or updated.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#labelOptions)
     */
    @JsonProperty()
    declare labelOptions?: SketchLabelOptionsPropertiesConfiguration;
    /**
     * An array of editing configurations for individual layers.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#layerInfos)
     */
    @JsonProperty()
    declare layerInfos?: EditorLayerInfoConfiguration[];
    /**
     * The [SnappingOptions](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html) for editing.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#snappingOptions)
     */
    @JsonProperty()
    declare snappingOptions?: SnappingOptionsPropertiesConfiguration;
    /**
     * This property allows customization of supporting Editor widgets and their default behavior.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#supportingWidgetDefaults)
     */
    // @JsonProperty()
    // declare supportingWidgetDefaults?: SupportingWidgetDefaults;
    /**
     * Options to configure the tooltip shown next to the cursor when creating or updating graphics.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#tooltipOptions)
     */
    @JsonProperty()
    declare tooltipOptions?: SketchTooltipOptionsPropertiesConfiguration;
    /**
     * Options to configure how values are displayed and input when creating or updating graphics.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#valueOptions)
     */
    @JsonProperty()
    declare  valueOptions?: SketchValueOptionsConfiguration;
    /**
     * Indicates whether the Editor should default to use the deprecated [CreateWorkflow](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor-CreateWorkflow.html).
     *
     * @default false
     * @deprecated since version 4.23. Although new at 4.23, this property was introduced to help migrate from the legacy {@link module:esri/widgets/Editor/CreateWorkflow} to the updated {@link module:esri/widgets/Editor/CreateFeaturesWorkflow}. Once {@link module:esri/widgets/Editor/CreateWorkflow} is fully removed, this property will no longer be necessary.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#useDeprecatedCreateWorkflow)
     */
    @JsonProperty()
    declare useDeprecatedCreateWorkflow?: boolean;

    /**
     * The visible elements that are displayed within the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Editor.html#visibleElements)
     */
    @JsonProperty()
    declare visibleElements?: EditorVisibleElementsConfiguration;

    constructor() {
        super();
    }
}
